// @safetyqube/sq-ai-react — SqAiProvider.
//
// Thin React binding over @safetyqube/sq-ai-client: it owns ONE transport +
// the conversation store, and exposes them via context. It is the relocation of
// the testing UI's socket lifecycle (SqAiPage's getSocket/setAuth/refreshAuth +
// connection tracking) and conversation hook (useSqAiConversation's reducer +
// event subscription + send/cancel) — behaviour preserved, no styled UI.
//
// Authored with React.createElement (NO JSX) so it bundles straight from
// node_modules in the testing UI's CRA build without a transpile step.

import React, {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from "react";
import {
  createSqAiTransport,
  reducer,
  initial,
  eventToActions,
  buildTurnEmit,
  cancelEmit,
  STREAMED_EVENTS,
} from "@safetyqube/sq-ai-client";

export const SqAiContext = createContext(null);

export function SqAiProvider(props) {
  const {
    conversationId,
    tenantSchema,
    context,
    mode = "chart", // chart-mode default; overridable (Phase 1.5) with zero engine changes
    auth = {},
    uiMode = "prod",
    devSql = true,
    getToken, // optional JWT getter; opens the socket AND stamps every send (see readAuthToken)
    namespace = "/ai-assistant",
    baseUrl = "", // sq-ai server origin; "" = same-origin (the testing UI)
    turnExtras, // optional fields spread onto the OPENING turn's payload only (the compare screen's model override)
    children,
  } = props;
  // `webSearch` is what this host ASKS for and may only narrow the engine's ceiling; absent
  // means "no opinion" (see createSqAiTransport). It rides `auth` with the rest because the
  // namespace reads it off the handshake, so changing it needs a reconnect.
  const { env, confidentialAccess, userTz, webSearch } = auth;

  // One transport for this provider instance, created once.
  const transportRef = useRef(null);
  if (!transportRef.current) {
    transportRef.current = createSqAiTransport({
      namespace,
      baseUrl,
      ...(getToken ? { getToken } : {}),
      env: env != null ? env : "dev",
      uiMode,
      confidentialAccess: confidentialAccess === true,
      userTz: userTz != null ? userTz : "Asia/Calcutta",
      devSql,
      webSearch,
    });
  }
  const transport = transportRef.current;

  const [socket, setSocket] = useState(null);
  const [connected, setConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  // What the SERVER says this session may see. Held here rather than derived in a page: the rule
  // that decides it filters rows on the server, and a second copy in the UI would be free to
  // drift from it — a header claiming "full access" while the engine scoped the answer is worse
  // than no header at all. Null until the server says, so the UI shows nothing rather than guessing.
  const [sessionPosture, setSessionPosture] = useState(null);

  // Mount: stamp auth BEFORE creating the socket so the first handshake carries
  // the right env/conf/tz, then track connection status (UI gating).
  useEffect(() => {
    transport.setAuth({ env, confidentialAccess, userTz, webSearch });
    const s = transport.getSocket();
    setSocket(s);
    // Registered in the SAME tick as getSocket(), before any network round-trip can complete, so
    // the connect-time posture cannot arrive before something is listening for it.
    const onSession = (p) => setSessionPosture(p || null);
    s.on("ai:session", onSession);
    const onConnect = () => { setConnected(true); setConnectionError(null); };
    const onDisconnect = () => setConnected(false);
    const onConnectError = (err) => {
      setConnected(false);
      // `status` rides only an expired session's refusal ({ status: 401, code: "session_expired" }, contract ErrorEvent).
      const data = (err && err.data) || {};
      setConnectionError({ code: data.code || "connect_error", ...(data.status ? { status: data.status } : {}), message: err && err.message });
    };
    if (s.connected) setConnected(true);
    s.on("connect", onConnect);
    s.on("disconnect", onDisconnect);
    s.on("connect_error", onConnectError);
    return () => {
      s.off("ai:session", onSession);
      s.off("connect", onConnect);
      s.off("disconnect", onDisconnect);
      s.off("connect_error", onConnectError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-handshake when env / confidentialAccess / userTz / webSearch changes (the namespace
  // reads them off the handshake, so changing them mid-session needs a reconnect).
  useEffect(() => {
    transport.setAuth({ env, confidentialAccess, userTz, webSearch });
    transport.refreshAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [env, confidentialAccess, userTz, webSearch]);

  // Conversation state machine.
  const [state, dispatch] = useReducer(reducer, conversationId, initial);

  // The picker's context object rides every turn; kept in a ref so `send` stays
  // stable and always reads the latest pinned scope without re-subscribing.
  const contextRef = useRef(context);
  useEffect(() => { contextRef.current = context; }, [context]);

  // Same ref treatment for getToken, and for the same reason — but here it is also what
  // keeps `send` from closing over a stale getter when the host swaps it on a re-render.
  const getTokenRef = useRef(getToken);
  useEffect(() => { getTokenRef.current = getToken; }, [getToken]);

  // And for turnExtras: read at send time, so a host re-rendering with a new object never re-creates
  // `send`. Absent — every host but the compare screen — the payload is unchanged.
  const turnExtrasRef = useRef(turnExtras);
  useEffect(() => { turnExtrasRef.current = turnExtras; }, [turnExtras]);

  // CALLED FRESH ON EVERY SEND. Never memoised, never stored: holding on to one token is
  // precisely the staleness the per-message token exists to fix, and a cache here would
  // reproduce it one layer up. The host owns where the token lives — this asks for it and
  // knows nothing about its storage.
  //
  // Absent, falsy, or throwing all mean the SAME thing: send without one. The server then
  // runs the turn on the handshake identity, so a getter that fails must degrade the turn's
  // freshness, never the turn itself.
  const readAuthToken = () => {
    const get = getTokenRef.current;
    if (typeof get !== "function") return undefined;
    try {
      const token = get();
      return typeof token === "string" && token ? token : undefined;
    } catch (err) {
      return undefined;
    }
  };

  useEffect(() => {
    dispatch({ type: "reset", id: conversationId });
  }, [conversationId]);

  useEffect(() => {
    if (!socket) return undefined;
    const handlers = {};
    for (const ev of STREAMED_EVENTS) {
      handlers[ev] = (p) => {
        const actions = eventToActions(ev, p, conversationId);
        for (const a of actions) dispatch(a);
      };
    }
    for (const ev of STREAMED_EVENTS) socket.on(ev, handlers[ev]);
    return () => {
      for (const ev of STREAMED_EVENTS) socket.off(ev, handlers[ev]);
    };
  }, [socket, conversationId]);

  const send = useCallback(
    (text) => {
      if (!socket || !text || !tenantSchema) return;
      const isFirst = state.messages.length === 0;
      const ctx = contextRef.current || undefined;
      dispatch({ type: "send", text, ts: Date.now() });
      const { event, payload } = buildTurnEmit({ isFirst, conversationId, text, tenantSchema, mode, context: ctx, authToken: readAuthToken(), extras: turnExtrasRef.current });
      socket.emit(event, payload);
    },
    [socket, conversationId, tenantSchema, state.messages.length, mode],
  );

  const cancel = useCallback(() => {
    if (!socket) return;
    const { event, payload } = cancelEmit(conversationId);
    socket.emit(event, payload);
  }, [socket, conversationId]);

  const value = useMemo(
    () => ({ state, send, cancel, connected, connectionError, socket, sessionPosture }),
    [state, send, cancel, connected, connectionError, socket, sessionPosture],
  );

  return React.createElement(SqAiContext.Provider, { value }, children);
}
